import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Form,
  Modal,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import atlProyeccionesService from 'services/atlProyeccionesService';
import clientService from 'services/clientService';
import { useAuth } from 'contexts/AuthContext';

const MEDIOS = ['TV', 'CABLE', 'RADIO', 'OOH', 'INTERNET'];
const FRANJAS = [
  { value: '', label: 'Sin franja' },
  { value: 'AAA', label: 'AAA — Prime (19-23h)' },
  { value: 'AA', label: 'AA — Tarde (12-19h)' },
  { value: 'A', label: 'A — Mañana (06-12h)' },
  { value: 'Late', label: 'Late — Trasnoche (23-06h)' },
  { value: 'N/D', label: 'Sin Horario' },
];
const ESTADOS = ['BORRADOR', 'ACTIVO', 'CERRADO'];
const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const fmtMoney = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtNum = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const emptyDetalle = () => ({
  medio: 'TV',
  canal: '',
  franja: '',
  inversionProyectada: 0,
  trpsProyectados: 0,
  spotsProyectados: 0,
  observaciones: '',
});

const emptyHeader = () => ({
  clientId: '',
  marca: '',
  brandKey: null,
  periodoYear: new Date().getFullYear(),
  periodoMonth: new Date().getMonth() + 1,
  nombre: '',
  descripcion: '',
  estado: 'BORRADOR',
  monedaCode: 'USD',
  detalles: [emptyDetalle()],
});

const ATLProyeccionesPage = () => {
  const { user } = useAuth();
  const [proyecciones, setProyecciones] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    clientId: '',
    year: new Date().getFullYear(),
    month: '',
    estado: '',
  });

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showComparativo, setShowComparativo] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadProyecciones = useCallback(async () => {
    setLoading(true);
    try {
      const data = await atlProyeccionesService.list(filters);
      setProyecciones(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Error al cargar proyecciones');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadClients = useCallback(async () => {
    try {
      const data = await clientService.getAllClients();
      setClients(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      console.warn('No se pudieron cargar los clientes', e);
    }
  }, []);

  useEffect(() => { loadProyecciones(); }, [loadProyecciones]);
  useEffect(() => { loadClients(); }, [loadClients]);

  const handleOpenCreate = () => {
    setEditing({
      ...emptyHeader(),
      clientId: filters.clientId || user?.companyId || '',
    });
    setShowModal(true);
  };

  const handleEdit = async (id) => {
    try {
      const data = await atlProyeccionesService.getById(id);
      setEditing({
        ...data,
        detalles: data.detalles && data.detalles.length > 0 ? data.detalles : [emptyDetalle()],
      });
      setShowModal(true);
    } catch (e) {
      toast.error('Error al cargar proyección');
    }
  };

  const handleSave = async () => {
    if (!editing?.clientId || !editing?.nombre || !editing?.periodoYear || !editing?.periodoMonth) {
      toast.warning('Completa cliente, nombre, año y mes');
      return;
    }
    if (!editing.detalles || editing.detalles.length === 0) {
      toast.warning('Agrega al menos un detalle');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        clientId: parseInt(editing.clientId),
        periodoYear: parseInt(editing.periodoYear),
        periodoMonth: parseInt(editing.periodoMonth),
        detalles: editing.detalles.map(d => ({
          ...d,
          franja: d.franja || null,
          inversionProyectada: Number(d.inversionProyectada) || 0,
          trpsProyectados: Number(d.trpsProyectados) || 0,
          spotsProyectados: Number(d.spotsProyectados) || 0,
        })),
      };
      if (editing.id) {
        await atlProyeccionesService.update(editing.id, payload);
        toast.success('Proyección actualizada');
      } else {
        await atlProyeccionesService.create(payload);
        toast.success('Proyección creada');
      }
      setShowModal(false);
      setEditing(null);
      loadProyecciones();
    } catch (e) {
      toast.error('Error al guardar: ' + (e?.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta proyección?')) return;
    try {
      await atlProyeccionesService.remove(id);
      toast.success('Eliminada');
      loadProyecciones();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const handleShowComparativo = async (id) => {
    try {
      const data = await atlProyeccionesService.getComparativo(id);
      setShowComparativo(data);
    } catch (e) {
      toast.error('Error al cargar comparativo');
    }
  };

  const addDetalle = () => {
    setEditing(prev => ({ ...prev, detalles: [...(prev.detalles || []), emptyDetalle()] }));
  };

  const updateDetalle = (idx, field, value) => {
    setEditing(prev => {
      const detalles = [...prev.detalles];
      detalles[idx] = { ...detalles[idx], [field]: value };
      return { ...prev, detalles };
    });
  };

  const removeDetalle = (idx) => {
    setEditing(prev => {
      const detalles = prev.detalles.filter((_, i) => i !== idx);
      return { ...prev, detalles: detalles.length > 0 ? detalles : [emptyDetalle()] };
    });
  };

  const totalDetalle = useMemo(() => {
    if (!editing?.detalles) return { inv: 0, trps: 0, spots: 0 };
    return editing.detalles.reduce((acc, d) => ({
      inv: acc.inv + (Number(d.inversionProyectada) || 0),
      trps: acc.trps + (Number(d.trpsProyectados) || 0),
      spots: acc.spots + (Number(d.spotsProyectados) || 0),
    }), { inv: 0, trps: 0, spots: 0 });
  }, [editing?.detalles]);

  const clientName = (clientId) => {
    const c = clients.find(x => x.id === clientId || x.companyId === clientId);
    return c?.companyName || c?.name || `Cliente ${clientId}`;
  };

  const estadoBadge = (estado) => {
    switch (estado) {
      case 'ACTIVO': return <Badge bg="success">ACTIVO</Badge>;
      case 'CERRADO': return <Badge bg="secondary">CERRADO</Badge>;
      default: return <Badge bg="warning" text="dark">BORRADOR</Badge>;
    }
  };

  return (
    <Container fluid className="px-3 pb-4">
      <div className="mb-3">
        <Row className="align-items-center">
          <Col>
            <h3 className="mb-1">
              <FontAwesomeIcon icon="chart-line" className="me-2 text-primary" />
              Proyecciones ATL
            </h3>
            <p className="text-muted mb-0 small">
              Planes de inversión y TRPs proyectados que alimentan los comparativos del dashboard ATL.
            </p>
          </Col>
          <Col xs="auto">
            <Button variant="primary" onClick={handleOpenCreate}>
              <FontAwesomeIcon icon="plus" className="me-1" /> Nueva proyección
            </Button>
          </Col>
        </Row>
      </div>

      {/* Filtros */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small mb-1">Cliente</Form.Label>
              <Form.Select size="sm" value={filters.clientId} onChange={(e) => setFilters({ ...filters, clientId: e.target.value })}>
                <option value="">Todos</option>
                {clients.map(c => (
                  <option key={c.id || c.companyId} value={c.id || c.companyId}>{c.companyName || c.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Año</Form.Label>
              <Form.Control size="sm" type="number" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Mes</Form.Label>
              <Form.Select size="sm" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })}>
                <option value="">Todos</option>
                {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Estado</Form.Label>
              <Form.Select size="sm" value={filters.estado} onChange={(e) => setFilters({ ...filters, estado: e.target.value })}>
                <option value="">Todos</option>
                {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button size="sm" variant="outline-primary" onClick={loadProyecciones}>
                <FontAwesomeIcon icon="search" className="me-1" /> Aplicar filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : proyecciones.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon icon="folder-open" size="3x" className="mb-2 opacity-50" />
              <p className="mb-0">No hay proyecciones. Crea la primera con el botón superior.</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0 small">
              <thead className="bg-light">
                <tr>
                  <th>Cliente</th>
                  <th>Período</th>
                  <th>Plan</th>
                  <th>Marca</th>
                  <th>Estado</th>
                  <th className="text-end">Inv. Proy.</th>
                  <th className="text-end">TRPs Proy.</th>
                  <th className="text-end">Spots</th>
                  <th className="text-center">Detalles</th>
                  <th className="text-center" style={{ width: 180 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proyecciones.map(p => (
                  <tr key={p.id}>
                    <td>{clientName(p.clientId)}</td>
                    <td><Badge bg="info" pill>{MESES[p.periodoMonth]} {p.periodoYear}</Badge></td>
                    <td className="fw-semibold">{p.nombre}</td>
                    <td>{p.marca || <span className="text-muted">—</span>}</td>
                    <td>{estadoBadge(p.estado)}</td>
                    <td className="text-end fw-semibold">{fmtMoney(p.totalInversion)}</td>
                    <td className="text-end">{fmtNum(p.totalTRPs)}</td>
                    <td className="text-end">{fmtNum(p.totalSpots)}</td>
                    <td className="text-center"><Badge bg="light" text="dark">{p.detalles?.length || 0}</Badge></td>
                    <td className="text-center">
                      <Button size="sm" variant="link" className="px-2 py-0" title="Comparar vs Real" onClick={() => handleShowComparativo(p.id)}>
                        <FontAwesomeIcon icon="chart-bar" />
                      </Button>
                      <Button size="sm" variant="link" className="px-2 py-0" title="Editar" onClick={() => handleEdit(p.id)}>
                        <FontAwesomeIcon icon="pencil-alt" />
                      </Button>
                      <Button size="sm" variant="link" className="px-2 py-0 text-danger" title="Eliminar" onClick={() => handleDelete(p.id)}>
                        <FontAwesomeIcon icon="trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Crear/Editar */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editing?.id ? 'pencil-alt' : 'plus'} className="me-2" />
            {editing?.id ? 'Editar' : 'Nueva'} Proyección ATL
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <>
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Form.Label className="small mb-1">Cliente *</Form.Label>
                  <Form.Select size="sm" value={editing.clientId} onChange={(e) => setEditing({ ...editing, clientId: e.target.value })}>
                    <option value="">Seleccionar...</option>
                    {clients.map(c => (
                      <option key={c.id || c.companyId} value={c.id || c.companyId}>{c.companyName || c.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Año *</Form.Label>
                  <Form.Control size="sm" type="number" value={editing.periodoYear} onChange={(e) => setEditing({ ...editing, periodoYear: e.target.value })} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Mes *</Form.Label>
                  <Form.Select size="sm" value={editing.periodoMonth} onChange={(e) => setEditing({ ...editing, periodoMonth: e.target.value })}>
                    {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Moneda</Form.Label>
                  <Form.Select size="sm" value={editing.monedaCode} onChange={(e) => setEditing({ ...editing, monedaCode: e.target.value })}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="COP">COP</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-1">Estado</Form.Label>
                  <Form.Select size="sm" value={editing.estado} onChange={(e) => setEditing({ ...editing, estado: e.target.value })}>
                    {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                  </Form.Select>
                </Col>
              </Row>

              <Row className="g-2 mb-3">
                <Col md={8}>
                  <Form.Label className="small mb-1">Nombre del plan *</Form.Label>
                  <Form.Control size="sm" value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} placeholder="Ej: Plan ATL Mayo 2026 — Tigo" />
                </Col>
                <Col md={4}>
                  <Form.Label className="small mb-1">Marca</Form.Label>
                  <Form.Control size="sm" value={editing.marca || ''} onChange={(e) => setEditing({ ...editing, marca: e.target.value })} placeholder="Ej: Tigo, Movistar..." />
                </Col>
              </Row>

              <Row className="g-2 mb-3">
                <Col>
                  <Form.Label className="small mb-1">Descripción</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={2} value={editing.descripcion || ''} onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })} />
                </Col>
              </Row>

              <Card className="border">
                <Card.Header className="py-2 d-flex justify-content-between align-items-center bg-light">
                  <h6 className="mb-0">
                    <FontAwesomeIcon icon="list" className="me-2" />
                    Detalle por medio / canal / franja
                  </h6>
                  <Button size="sm" variant="outline-primary" onClick={addDetalle}>
                    <FontAwesomeIcon icon="plus" className="me-1" /> Agregar fila
                  </Button>
                </Card.Header>
                <Card.Body className="p-2">
                  <Table size="sm" responsive borderless className="mb-0 align-middle">
                    <thead>
                      <tr className="small text-muted">
                        <th style={{ width: 110 }}>Medio</th>
                        <th>Canal/Emisora</th>
                        <th style={{ width: 180 }}>Franja</th>
                        <th style={{ width: 130 }} className="text-end">Inv. Proy. ($)</th>
                        <th style={{ width: 110 }} className="text-end">TRPs Proy.</th>
                        <th style={{ width: 110 }} className="text-end">Spots</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(editing.detalles || []).map((d, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Select size="sm" value={d.medio} onChange={(e) => updateDetalle(idx, 'medio', e.target.value)}>
                              {MEDIOS.map(m => <option key={m} value={m}>{m}</option>)}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control size="sm" placeholder="Ej: ECUAVISA (opcional)" value={d.canal || ''} onChange={(e) => updateDetalle(idx, 'canal', e.target.value)} />
                          </td>
                          <td>
                            <Form.Select size="sm" value={d.franja || ''} onChange={(e) => updateDetalle(idx, 'franja', e.target.value)} disabled={!['TV', 'CABLE'].includes(d.medio)}>
                              {FRANJAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </Form.Select>
                          </td>
                          <td>
                            <InputGroup size="sm">
                              <InputGroup.Text>$</InputGroup.Text>
                              <Form.Control type="number" step="0.01" className="text-end" value={d.inversionProyectada} onChange={(e) => updateDetalle(idx, 'inversionProyectada', e.target.value)} />
                            </InputGroup>
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" step="0.01" className="text-end" value={d.trpsProyectados} onChange={(e) => updateDetalle(idx, 'trpsProyectados', e.target.value)} />
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" className="text-end" value={d.spotsProyectados} onChange={(e) => updateDetalle(idx, 'spotsProyectados', e.target.value)} />
                          </td>
                          <td>
                            <Button size="sm" variant="link" className="text-danger px-1" onClick={() => removeDetalle(idx)}>
                              <FontAwesomeIcon icon="times" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="fw-semibold bg-light">
                        <td colSpan={3} className="text-end">Totales:</td>
                        <td className="text-end">{fmtMoney(totalDetalle.inv)}</td>
                        <td className="text-end">{fmtNum(totalDetalle.trps)}</td>
                        <td className="text-end">{fmtNum(totalDetalle.spots)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </Table>
                </Card.Body>
              </Card>

              <Alert variant="info" className="mt-3 mb-0 small">
                <FontAwesomeIcon icon="info-circle" className="me-1" />
                La <strong>inversión real</strong> sale del campo <code>Tariff</code> de Infomedia.
                Los <strong>TRPs naturales</strong> son la suma del campo <code>RPP</code>.
                Las proyecciones <strong>ACTIVAS</strong> reemplazarán las columnas "Proyectado" del dashboard ATL del cliente.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving && <Spinner size="sm" className="me-1" />}
            {editing?.id ? 'Actualizar' : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Comparativo Real vs Proyectado */}
      <Modal show={!!showComparativo} onHide={() => setShowComparativo(null)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon="chart-bar" className="me-2 text-success" />
            Comparativo Real vs Proyectado
            {showComparativo?.proyeccion && (
              <small className="ms-2 text-muted">— {showComparativo.proyeccion.nombre}</small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showComparativo && (
            <>
              <Row className="g-2 mb-3">
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Body className="text-center py-2">
                      <div className="small text-uppercase text-muted">Inversión Proy. → Real</div>
                      <div className="d-flex justify-content-center align-items-baseline gap-2">
                        <div className="fw-bold">{fmtMoney(showComparativo.resumen.inversionProy)}</div>
                        <FontAwesomeIcon icon="arrow-right" className="text-muted small" />
                        <div className="fw-bold text-primary">{fmtMoney(showComparativo.resumen.inversionReal)}</div>
                      </div>
                      <Badge bg={showComparativo.resumen.cumplimientoInv >= 90 ? 'success' : showComparativo.resumen.cumplimientoInv >= 70 ? 'warning' : 'danger'}>
                        {showComparativo.resumen.cumplimientoInv}% cumplimiento
                      </Badge>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Body className="text-center py-2">
                      <div className="small text-uppercase text-muted">TRPs Proy. → Real</div>
                      <div className="d-flex justify-content-center align-items-baseline gap-2">
                        <div className="fw-bold">{fmtNum(showComparativo.resumen.trpsProy)}</div>
                        <FontAwesomeIcon icon="arrow-right" className="text-muted small" />
                        <div className="fw-bold text-primary">{fmtNum(showComparativo.resumen.trpsReal)}</div>
                      </div>
                      <Badge bg={showComparativo.resumen.cumplimientoTRPs >= 90 ? 'success' : showComparativo.resumen.cumplimientoTRPs >= 70 ? 'warning' : 'danger'}>
                        {showComparativo.resumen.cumplimientoTRPs}% cumplimiento
                      </Badge>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Body className="text-center py-2">
                      <div className="small text-uppercase text-muted">Spots Proy. → Real</div>
                      <div className="d-flex justify-content-center align-items-baseline gap-2">
                        <div className="fw-bold">{fmtNum(showComparativo.resumen.spotsProy)}</div>
                        <FontAwesomeIcon icon="arrow-right" className="text-muted small" />
                        <div className="fw-bold text-primary">{fmtNum(showComparativo.resumen.spotsReal)}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Table size="sm" hover responsive>
                <thead className="bg-light">
                  <tr>
                    <th>Medio</th>
                    <th>Canal</th>
                    <th>Franja</th>
                    <th className="text-end">Inv. Proy.</th>
                    <th className="text-end">Inv. Real</th>
                    <th className="text-end">% Inv.</th>
                    <th className="text-end">TRPs Proy.</th>
                    <th className="text-end">TRPs Real</th>
                    <th className="text-end">% TRPs</th>
                  </tr>
                </thead>
                <tbody>
                  {showComparativo.detalles.map((d) => (
                    <tr key={d.id}>
                      <td><Badge bg="info" pill>{d.medio}</Badge></td>
                      <td>{d.canal || '—'}</td>
                      <td>{d.franja || '—'}</td>
                      <td className="text-end">{fmtMoney(d.inversionProyectada)}</td>
                      <td className="text-end fw-semibold">{fmtMoney(d.invReal)}</td>
                      <td className="text-end">
                        <Badge bg={d.cumplimientoInv >= 90 ? 'success' : d.cumplimientoInv >= 70 ? 'warning' : 'danger'}>
                          {d.cumplimientoInv}%
                        </Badge>
                      </td>
                      <td className="text-end">{fmtNum(d.trpsProyectados)}</td>
                      <td className="text-end fw-semibold">{fmtNum(d.trpsReal)}</td>
                      <td className="text-end">
                        <Badge bg={d.cumplimientoTRPs >= 90 ? 'success' : d.cumplimientoTRPs >= 70 ? 'warning' : 'danger'}>
                          {d.cumplimientoTRPs}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowComparativo(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ATLProyeccionesPage;
